import { SxProps, Theme } from "@mui/material";

export const containerStyle: SxProps<Theme> = (theme) => ({
  mx: { xs: "20px", sm: "50px", md: "50px" },
  mt: { xs: "4px", sm: "4px", md: "4px" },
});

export const titleStyle: SxProps<Theme> = (theme) => ({
  fontWeight: 600,
  fontSize: "16px",
  color:
    theme.palette.leaderBoardBlock?.textColor ?? theme.palette.text.primary,
  mt: 4,
  mb: 1.5,
});

export const scrollContainerStyle: SxProps<Theme> = (theme) => ({
  display: "flex",
  gap: 1.5,
  overflowX: "auto",
  overflowY: "hidden",
  pb: 1,
  "&::-webkit-scrollbar": {
    height: 6,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.2)",
    borderRadius: 3,
  },
});

export const avatarWrapperStyle: SxProps<Theme> = {
  mb: 1,
  display: "inline-flex",
  p: 0,
  border: 0,
  background: "transparent",
};

export const getCardStyle = (): SxProps<Theme> => (theme) => {
  const borderColor =
    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "#E0E0E0";
  return {
    flexShrink: 0,
    minWidth: 170,
    maxWidth: 170,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: "transparent",
    border: `1px solid ${borderColor}`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 2px 8px rgba(0, 0, 0, 0.3)"
        : "0 2px 8px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    overflow: "visible",
    pt: 3,
  };
};

const RIBBON_GRADIENT =
  "linear-gradient(145deg, #FFF8DC 0%, #FFE066 35%, #FFD700 70%, #E6A800 100%)";

export const getCornerRibbonStyle = (): SxProps<Theme> => () => ({
  position: "absolute",
  top: 0,
  left: 4,
  width: 46,
  height: 40,
  background: RIBBON_GRADIENT,
  clipPath: "polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)",
  boxShadow: "0 2px 4px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.4)",
  zIndex: 1,
});

export const textBlockStyle: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary,
  textAlign: "center",
  "& p": { margin: 0 },
  "& .pl-sub": { fontSize: "9px", lineHeight: 1.25 },
});

export const modalContentStyle: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "fit-content",
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100vh - 24px)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 2,
  outline: "none",
  overflowY: "auto",
  overflowX: "hidden",
  boxSizing: "border-box",
  "@media (max-height: 500px) and (orientation: landscape)": {
    maxWidth: "calc(100vw - 12px)",
    maxHeight: "calc(100vh - 12px)",
    p: 1,
  },
};

export const modalHeaderStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 1.5,
  position: "relative",
  "@media (max-height: 500px) and (orientation: landscape)": {
    mb: 0.75,
  },
};

export const modalTitleStyle: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  fontSize: "1rem",
  textAlign: "center",
  color:
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary,
  "@media (max-height: 500px) and (orientation: landscape)": {
    fontSize: "0.92rem",
  },
});

export const modalCloseButtonStyle: SxProps<Theme> = {
  position: "absolute",
  right: 0,
};

export const modalVideoStyle: SxProps<Theme> = {
  width: "auto",
  height: "auto",
  maxWidth: "calc(100vw - 56px)",
  maxHeight: "calc(100vh - 260px)",
  borderRadius: 1.5,
  bgcolor: "#000",
  display: "block",
  mx: "auto",
  "@media (max-height: 900px)": {
    maxHeight: "calc(100vh - 300px)",
  },
  "@media (max-height: 820px)": {
    maxHeight: "calc(100vh - 330px)",
  },
  "@media (max-height: 500px) and (orientation: landscape)": {
    maxWidth: "calc(100vw - 24px)",
    maxHeight: "calc(100vh - 170px)",
  },
};

export const modalQuoteStyle: SxProps<Theme> = (theme) => ({
  mt: { xs: 1, sm: 1.25 },
  fontWeight: 700,
  fontStyle: "italic",
  fontSize: { xs: "0.65rem", sm: "0.98rem", md: "0.81rem" },
  lineHeight: 1.35,
  color:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.7)",
  textAlign: "center",
  whiteSpace: "normal",
  width: "fit-content",
  maxWidth: { xs: "min(88vw, 280px)", sm: "min(86vw, 760px)", md: "460px" },
  mx: "auto",
  display: { xs: "block", sm: "-webkit-box" },
  WebkitLineClamp: { xs: "unset", sm: 2 },
  WebkitBoxOrient: { xs: "unset", sm: "vertical" },
  "@media (max-height: 500px) and (orientation: landscape)": {
    mt: 0.55,
    mb: 2,
    fontSize: "0.45rem",
    lineHeight: 1.2,
    width: "200px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
});

export const modalFooterRowStyle: SxProps<Theme> = {
  mt: { xs: 1, sm: 1.5 },
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.9, sm: 1.25 },
  "@media (max-height: 500px) and (orientation: landscape)": {
    mt: 0.55,
    gap: 0.7,
  },
};

export const modalFooterAvatarWrapStyle: SxProps<Theme> = {
  flexShrink: 0,
  mr: 2,
  ml: 1,
};

export const modalFooterNameStyle: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  fontSize: { xs: "0.78rem", sm: "1rem" },
  lineHeight: 1.2,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.text.primary,
  "@media (max-height: 500px) and (orientation: landscape)": {
    fontSize: "0.82rem",
  },
});

export const modalFooterMetaStyle: SxProps<Theme> = (theme) => ({
  fontSize: { xs: "0.72rem", sm: "0.92rem" },
  lineHeight: { xs: 1.25, sm: 1.35 },
  overflowWrap: "anywhere",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.75)"
      : theme.palette.text.secondary,
  "@media (max-height: 500px) and (orientation: landscape)": {
    fontSize: "0.75rem",
    lineHeight: 1.2,
  },
});
